import React from "react";
import { motion } from "framer-motion";
import { contactModes, ContactFields, useContactForm } from "../hooks/useContactForm";
import { ledgerEase } from "../utils/motion";
const sizes = ["Under 250", "250–1,000", "1,000–5,000", "5,000+"];
const areas = ["Payroll operations", "HR technology", "Both payroll and HR technology", "Process & controls"];
type TextFieldProps = {
  id: keyof ContactFields;
  label: string;
  value: string;
  error?: string;
  type?: string;
  optional?: boolean;
  onChange: (value: string) => void;
};
function TextField({
  id,
  label,
  value,
  error,
  type = "text",
  optional,
  onChange
}: TextFieldProps) {
  return <div data-id="element-49" data-mp="lvqqi9p62bxd">
      <label htmlFor={`contact-${id}`} className="eyebrow text-charcoal/70" data-id="element-50" data-mp="lwa6hkv9aq8r">
        {label}
        {optional && <span className="ml-2 normal-case tracking-normal text-charcoal/50" data-id="element-51" data-mp="h1w95cx1a91g">Optional</span>}
      </label>
      <input id={`contact-${id}`} type={type} value={value} onChange={e => onChange(e.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? `contact-${id}-error` : undefined} className={`mt-3 w-full border-b bg-transparent py-3 text-lg text-charcoal transition-colors duration-150 focus:outline-none ${error ? "border-oxblood" : "border-stone focus:border-navy"}`} data-id="element-52" data-mp="3kkfo5dqpbieb" />
      {error && <p id={`contact-${id}-error`} className="mt-2 text-sm text-oxblood" data-id="element-53" data-mp="86hvhupsqjcw">
          {error}
        </p>}
    </div>;
}
type ContactFormProps = {
  serviceName?: string;
  initialValues?: Partial<ContactFields>;
};
export function ContactForm({
  serviceName,
  initialValues
}: ContactFormProps) {
  const {
    values,
    errors,
    status,
    update,
    submit,
    reset
  } = useContactForm(initialValues);
  if (status === "success") {
    return <motion.div role="status" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: ledgerEase
    }} className="border-t border-navy pt-10" data-id="element-54" data-mp="vv182mfvpias">
        <p className="font-display text-5xl text-navy" data-id="element-55" data-mp="2wunxmj3tcyo6">Received.</p>
        <p className="mt-6 max-w-md text-lg leading-relaxed text-charcoal/80" data-id="element-56" data-mp="2rwcne79lwm5f">
          Thank you, {values.name.split(" ")[0]}. A senior Constant operator will reply to {values.email} within one
          business day{values.mode === "Rescue" ? " — Rescue enquiries are answered the same day" : ""}.
        </p>
        <button type="button" onClick={reset} className="btn-ghost mt-10" data-id="element-57" data-mp="2d9b6455i5vgt">
          Send another note
        </button>
      </motion.div>;
  }
  return <form onSubmit={submit} noValidate className="border-t border-navy pt-10" aria-label="Talk to Constant" data-id="element-58" data-mp="2my1f40yymjpb">
      {serviceName && <div className="mb-10 flex items-baseline justify-between gap-4 border-l-2 border-oxblood bg-stone/40 px-5 py-4" data-id="element-59" data-mp="2i6t450hzeqa1">
          <p className="text-sm text-charcoal/80" data-id="element-60" data-mp="1ug79qc0wh4ii">
            Regarding <span className="font-display text-lg text-navy" data-id="element-61" data-mp="1pjp2n5i3ku30">{serviceName}</span>
          </p>
          {values.mode === "Rescue" && <p className="eyebrow text-oxblood" data-id="element-62" data-mp="4zbosfki8mq9">Same-day reply</p>}
        </div>}
      <div className="grid grid-cols-1 gap-x-8 gap-y-9 md:grid-cols-2" data-id="element-63" data-mp="9smu2c9o4vg2">
        <TextField id="name" label="Name" value={values.name} error={errors.name} onChange={v => update("name", v)} data-id="element-64" data-mp="3s649lt8n20l3" />
        <TextField id="email" label="Work email" type="email" value={values.email} error={errors.email} onChange={v => update("email", v)} data-id="element-65" data-mp="10ckmx6lktzv" />
        <TextField id="organization" label="Organization" value={values.organization} error={errors.organization} onChange={v => update("organization", v)} data-id="element-66" data-mp="sp61mx4tqbai" />
        <TextField id="role" label="Role" optional value={values.role} onChange={v => update("role", v)} data-id="element-67" data-mp="jny3cf2b5agf" />
      </div>

      <div className="mt-12" data-id="element-68" data-mp="2zj9qp0rgz99q">
        <label htmlFor="contact-area" className="eyebrow text-charcoal/70" data-id="element-69" data-mp="34gjg4geuzkxk">
          What is this about?
        </label>
        <select id="contact-area" value={values.area} onChange={e => update("area", e.target.value)} aria-invalid={Boolean(errors.area)} aria-describedby={errors.area ? "contact-area-error" : undefined} className={`mt-3 w-full appearance-none rounded-none border-b bg-transparent py-3 text-lg text-charcoal focus:outline-none ${errors.area ? "border-oxblood" : "border-stone focus:border-navy"}`} data-id="element-70" data-mp="3jqgegp37i15m">
          <option value="" data-id="element-71" data-mp="2il08jq6xnhz">Select one</option>
          {areas.map(a => <option key={a} value={a} data-id="element-72" data-mp="3tnb8ovsg5fe3">
              {a}
            </option>)}
        </select>
        {errors.area && <p id="contact-area-error" className="mt-2 text-sm text-oxblood" data-id="element-73" data-mp="3oo84teo7df7p">
            {errors.area}
          </p>}
      </div>

      <fieldset className="mt-12" aria-describedby={errors.mode ? "contact-mode-error" : undefined} data-id="element-74" data-mp="2i6bbblr8sad7">
        <legend className="eyebrow text-charcoal/70" data-id="element-75" data-mp="235ny3nzfmq32">What situation are you in?</legend>
        <div className="mt-4 grid grid-cols-2 border-l border-t border-stone sm:grid-cols-5" data-id="element-76" data-mp="233tag1ws0jva">
          {contactModes.map(m => {
          const checked = values.mode === m;
          return <label key={m} className={`flex cursor-pointer items-center justify-center whitespace-nowrap border-b border-r border-stone px-3 py-4 text-sm font-medium transition-colors duration-150 ease-ledger has-[:focus-visible]:outline has-[:focus-visible]:outline-1 has-[:focus-visible]:outline-oxblood ${checked ? "bg-oxblood text-ivory" : "text-charcoal/80 hover:text-oxblood"}`} data-id="element-77" data-mp="2d1zasf2xphy1">
                <input type="radio" name="mode" value={m} checked={checked} onChange={() => update("mode", m)} className="sr-only" data-id="element-78" data-mp="30swfp7wbbezs" />
                {m}
              </label>;
        })}
        </div>
        {errors.mode && <p id="contact-mode-error" className="mt-2 text-sm text-oxblood" data-id="element-79" data-mp="tge7hr76os3b">
            {errors.mode}
          </p>}
        {values.mode === "Rescue" && <p className="mt-4 text-sm text-charcoal/75" data-id="element-80" data-mp="13dsaao06pid5">Rescue enquiries are answered the same business day.</p>}
      </fieldset>

      <div className="mt-12" data-id="element-81" data-mp="1hcs224izuddj">
        <label htmlFor="contact-size" className="eyebrow text-charcoal/70" data-id="element-82" data-mp="1hbqn3oyux6qj">
          Employees paid <span className="ml-2 normal-case tracking-normal text-charcoal/50" data-id="element-83" data-mp="1cdxgiutn6apq">Optional</span>
        </label>
        <select id="contact-size" value={values.size} onChange={e => update("size", e.target.value)} className="mt-3 w-full appearance-none rounded-none border-b border-stone bg-transparent py-3 text-lg text-charcoal focus:border-navy focus:outline-none" data-id="element-84" data-mp="1r2l9ctc3s6bz">
          <option value="" data-id="element-85" data-mp="1w0d19ncqzsg6">Select a range</option>
          {sizes.map(s => <option key={s} value={s} data-id="element-86" data-mp="2er9obj1q1fj7">
              {s}
            </option>)}
        </select>
      </div>

      <div className="mt-12" data-id="element-87" data-mp="2jnqnr9edfgbl">
        <label htmlFor="contact-message" className="eyebrow text-charcoal/70" data-id="element-88" data-mp="25qbt9q99gicr">
          The situation
        </label>
        <textarea id="contact-message" rows={4} value={values.message} onChange={e => update("message", e.target.value)} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? "contact-message-error" : undefined} placeholder="What’s happening, which systems you use, and when the next payroll runs." className={`mt-3 w-full resize-none border-b bg-transparent py-3 text-lg text-charcoal placeholder:text-charcoal/40 focus:outline-none ${errors.message ? "border-oxblood" : "border-stone focus:border-navy"}`} data-id="element-89" data-mp="2aodd4deiglpa" />
        {errors.message && <p id="contact-message-error" className="mt-2 text-sm text-oxblood" data-id="element-90" data-mp="1xi8survwbc7m">
            {errors.message}
          </p>}
      </div>

      <div className="mt-12 flex flex-wrap items-center gap-6" data-id="element-91" data-mp="1skpe7qvacxf5">
        <button type="submit" disabled={status === "submitting"} className="btn-primary disabled:opacity-70" data-id="element-92" data-mp="1ohaxexziwd0n">
          {status === "submitting" ? "Sending…" : "Send to Constant"}
        </button>
        <p className="text-sm text-charcoal/65" data-id="element-93" data-mp="1ek69asm4a7h7">Held in confidence. Never shared.</p>
      </div>
    </form>;
}